/**
 * Small helpers for the Home trip cards: friendly day words, the meeting point of a campus and
 * plain status words for bookings and lifts. Pure functions, no React.
 */
import { RIDES_AVAILABILITY_STATUS, RIDES_BOOKING_STATUS, type RidesAvailabilityView, type RidesBookingView, type RidesCampusView, type RidesVehicleView } from '@actschurch/ccms-sdk';
import { campusAddress } from '@/lib/lookups';
import { combineDateTime, formatTime, parseApiDate, plural } from '@/lib/format';
import type { PillKind } from '@/lib/copy';

export type Tone = PillKind | 'neutral';

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** Whole days from today to the date (0 = today, 1 = tomorrow, negative = past). */
export function daysFromToday(date: string | number | null | undefined, now = new Date()): number | null {
  const d = parseApiDate(date);
  if (!d) return null;
  return Math.round((startOfDay(d).getTime() - startOfDay(now).getTime()) / 86400000);
}

/** "Today", "Tomorrow", "In 3 days" or null (further away or past). */
export function dayWord(date: string | number | null | undefined): string | null {
  const n = daysFromToday(date);
  if (n === null || n < 0) return null;
  if (n === 0) return 'Today';
  if (n === 1) return 'Tomorrow';
  if (n < 7) return `In ${n} days`;
  return null;
}

export const isToday = (date: string | number | null | undefined) => daysFromToday(date) === 0;

/** "Silver Toyota Corolla Quest" */
export function carWords(v: RidesVehicleView | null | undefined): string {
  if (!v) return '';
  const model = (v.label || `${v.make} ${v.model}`).trim();
  return [v.colour?.trim(), model].filter(Boolean).join(' ');
}

/** The pick-up note for a campus (or its address) and a maps link. */
export function meetingPoint(c: RidesCampusView | undefined): { text: string; mapsUrl: string | null } | null {
  if (!c) return null;
  const address = campusAddress(c);
  const text = c.pickupInstructions?.trim() || address;
  if (!text) return null;
  const query = c.latitude && c.longitude ? `${c.latitude},${c.longitude}` : address ? `${address}` : '';
  return { text, mapsUrl: query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : null };
}

/** Departure moment for sorting (falls back to the service time, then midnight). */
export function tripMoment(t: { date: string; departureTime?: string | null; serviceTimeStart?: string | null }): number {
  return combineDateTime(t.date, t.departureTime ?? t.serviceTimeStart)?.getTime() ?? 0;
}

export function bookingStatus(b: RidesBookingView): { label: string; tone: Tone } {
  if (b.statusID === RIDES_BOOKING_STATUS.CancelledByDriver) return { label: 'Driver cancelled', tone: 'bad' };
  if (b.statusID === RIDES_BOOKING_STATUS.CancelledByRider) return { label: 'You cancelled', tone: 'neutral' };
  if (b.statusID === RIDES_BOOKING_STATUS.Completed) return { label: 'Done', tone: 'neutral' };
  if (b.statusID === RIDES_BOOKING_STATUS.NoShow) return { label: 'Missed', tone: 'warn' };
  if (b.driverArrivedAt) return { label: 'Your driver is here', tone: 'ok' };
  return { label: 'Confirmed', tone: 'ok' };
}

export function liftStatus(l: RidesAvailabilityView): { label: string; tone: Tone } {
  switch (l.statusID) {
    case RIDES_AVAILABILITY_STATUS.Arrived: return { label: "You've arrived", tone: 'ok' };
    case RIDES_AVAILABILITY_STATUS.Full: return { label: 'Full', tone: 'ok' };
    case RIDES_AVAILABILITY_STATUS.Closed: return { label: 'Closed for bookings', tone: 'neutral' };
    case RIDES_AVAILABILITY_STATUS.Cancelled: return { label: 'Cancelled', tone: 'neutral' };
    case RIDES_AVAILABILITY_STATUS.Completed: return { label: 'Done', tone: 'neutral' };
    default: return { label: l.seatsOpen > 0 ? `${plural(l.seatsOpen, 'seat')} free` : 'Full', tone: l.seatsOpen > 0 ? 'info' : 'ok' };
  }
}

/** "Naledi, Grace and Kagiso" (children by their own name on under-18 bookings). */
export function riderNames(bookings: RidesBookingView[] | null | undefined): string {
  const names = (bookings ?? [])
    .filter((b) => b.statusID === RIDES_BOOKING_STATUS.Confirmed)
    .map((b) => (b.forMinor ? b.childName?.trim() || 'a child' : b.riderName.split(' ')[0]));
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

export const timeOf = (t: { departureTime?: string | null; serviceTimeStart?: string | null }) => formatTime(t.departureTime ?? t.serviceTimeStart);
